import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './CodeSmellDetector.css';

const CodeSmellDetector = ({ code, language, onAnalysisComplete }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const analyzeCode = useCallback(async () => {
    if (!code || code.trim().length === 0) return;

    setLoading(true);
    try {
      const response = await api.post('/api/code-quality/analyze', {
        code: code,
        language: language || 'java'
      });
      setAnalysis(response.data);
      if (onAnalysisComplete) {
        onAnalysisComplete(response.data);
      }
    } catch (error) {
      console.error('Error analyzing code:', error);
    } finally {
      setLoading(false);
    }
  }, [code, language, onAnalysisComplete]);

  useEffect(() => {
    // Auto-analyze after user stops typing for 2 seconds
    const timer = setTimeout(() => {
      if (code && code.length > 50) {
        analyzeCode();
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [code, language, analyzeCode]);

  const getScoreColor = (score) => {
    if (score >= 90) return '#4caf50';
    if (score >= 75) return '#8bc34a';
    if (score >= 60) return '#ffc107';
    if (score >= 40) return '#ff9800';
    return '#f44336';
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#f44336';
      case 'major': return '#ff9800';
      case 'minor': return '#ffc107';
      default: return '#2196f3';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return '🚨';
      case 'major': return '⚠️';
      case 'minor': return '⚡';
      default: return 'ℹ️';
    }
  };

  if (!analysis && !loading) {
    return (
      <div className="code-smell-detector compact">
        <div className="detector-header">
          <span className="detector-icon">✨</span>
          <span>Code Quality</span>
        </div>
        <div className="waiting-message">
          Write code to see quality analysis...
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="code-smell-detector compact">
        <div className="detector-header">
          <span className="detector-icon">✨</span>
          <span>Code Quality</span>
        </div>
        <div className="analyzing-message">
          <div className="spinner"></div>
          Analyzing code...
        </div>
      </div>
    );
  }

  return (
    <div className="code-smell-detector">
      <div className="detector-header" onClick={() => setShowDetails(!showDetails)}>
        <div className="header-left">
          <span className="detector-icon">✨</span>
          <span>Code Quality</span>
        </div>
        <div className="header-right">
          <div className="score-badge" style={{ borderColor: getScoreColor(analysis.score) }}>
            <span className="score-value" style={{ color: getScoreColor(analysis.score) }}>
              {analysis.score.toFixed(0)}
            </span>
            <span className="score-label">/100</span>
          </div>
          <button className="toggle-btn">{showDetails ? '▼' : '▶'}</button>
        </div>
      </div>

      <div className="rating-badge" style={{ 
        background: `linear-gradient(135deg, ${getScoreColor(analysis.score)}22, ${getScoreColor(analysis.score)}44)`
      }}>
        <span style={{ color: getScoreColor(analysis.score) }}>{analysis.rating}</span>
      </div>

      {showDetails && (
        <div className="detector-content">
          {analysis.smells && analysis.smells.length > 0 ? (
            <div className="smells-list">
              <h4>🔍 Code Smells Detected ({analysis.smells.length})</h4>
              {analysis.smells.map((smell, index) => (
                <div key={index} className="smell-item" style={{ borderLeftColor: getSeverityColor(smell.severity) }}>
                  <div className="smell-header">
                    <span className="smell-icon">{getSeverityIcon(smell.severity)}</span>
                    <span className="smell-type">{smell.type}</span>
                    <span className="smell-severity" style={{ color: getSeverityColor(smell.severity) }}>
                      {smell.severity}
                    </span>
                  </div>
                  <div className="smell-description">{smell.description}</div>
                  {smell.fix && (
                    <div className="smell-fix">
                      💡 <strong>Fix:</strong> {smell.fix}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-smells">
              <span className="success-icon">✅</span>
              <p>No code smells detected! Great job!</p>
            </div>
          )}

          {analysis.suggestions && analysis.suggestions.length > 0 && (
            <div className="suggestions">
              <h4>💡 Suggestions</h4>
              <ul>
                {analysis.suggestions.map((suggestion, index) => (
                  <li key={index}>{suggestion}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Visual smell indicator */}
      {analysis.smells && analysis.smells.length > 0 && (
        <div className="smell-clouds">
          {analysis.smells.slice(0, 3).map((smell, index) => (
            <div 
              key={index} 
              className="smell-cloud"
              style={{ 
                left: `${20 + index * 30}%`,
                animationDelay: `${index * 0.5}s`
              }}
            >
              💨
            </div>
          ))}
        </div>
      )}

      {analysis.score >= 90 && (
        <div className="perfume-effect">
          <div className="sparkle">✨</div>
          <div className="sparkle">✨</div>
          <div className="sparkle">✨</div>
        </div>
      )}
    </div>
  );
};

export default CodeSmellDetector;


